import { useEffect, useRef, useState } from 'react';
import { useAdobeImsAuth } from './auth/useAdobeImsAuth';
import { Header } from './components/Header';
import { TabNav } from './components/TabNav';
import { FilterBar } from './components/FilterBar';
import { VPPanel } from './components/panels/VPPanel';
import { SDPanel } from './components/panels/SDPanel';
import { ODPanel } from './components/panels/ODPanel';
import { AvailabilityTrackerPanel } from './components/panels/AvailabilityTrackerPanel';
import { HeatmapModal } from './components/HeatmapModal';
import { UploadCsvModal } from './components/UploadCsvModal';
import { ManageModal } from './components/ManageModal';
import { AiChatSidebar } from './components/AiChatSidebar';
import { useAppState } from './hooks/useAppState';
import { RequestsProvider, useRequests } from './context/RequestsContext';
import { OrgProvider } from './context/OrgContext';
import { fetchDeals, fetchTouchpoints, triggerSync, emitEvent } from './utils/database';
import { canOpenManage } from './utils/manageAccess';
import { transformRequestResponse, transformTouchpointResponse } from './utils/transformers';
import { friendlyTime } from './utils/friendlyTime';
import { getLatestFY } from './utils/filter';
import type { TabState } from './types';
function Spinner({
  size = 16,
  color = '#6B7280'
}: {
  size?: number;
  color?: string;
}) {
  return <svg width={size} height={size} viewBox="0 0 24 24" className="animate-spin" style={{
    color
  }} data-id="element-0" data-mp="2zl3bdjx5744s">
      <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25" data-id="element-1" data-mp="33mowttnqsqql" />
      <path d="M12 2a10 10 0 0 1 10 10" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" data-id="element-2" data-mp="38m1f013o59mu" />
    </svg>;
}
const ADOBE_LOGO_URL = 'https://cdn.magicpatterns.com/uploads/6yi4FNzoPfGEun9849jQCu/Screenshot_2026-02-03_at_7.33.48_PM.png';

// Inner component has access to DealsProvider context
function Dashboard() {
  const {
    accessLevel,
    error: authError,
    isLoading: isAuthLoading,
    isReady: isAuthReady,
    isSignedIn,
    signIn,
    signOut,
    user
  } = useAdobeImsAuth();
  const {
    state,
    currentTab,
    updateTab,
    switchTab,
    drillRegion
  } = useAppState();
  const {
    requests,
    setRequests,
    touchpoints,
    setTouchpoints,
    setSyncMeta,
    syncStatus,
    lastSynced,
    isLiveData
  } = useRequests();

  // Default the FY filter to the latest fiscal year present in the data, once, the first
  // time real data loads — across all three tabs. Doesn't override a later manual choice.
  const hasAutoSelectedFY = useRef(false);
  useEffect(() => {
    if (hasAutoSelectedFY.current || requests.length === 0) return;
    const latestFY = getLatestFY(requests);
    if (!latestFY) return;
    hasAutoSelectedFY.current = true;
    updateTab('vp', {
      fy: latestFY
    });
    updateTab('sd', {
      fy: latestFY
    });
    updateTab('od', {
      fy: latestFY
    });
  }, [requests, updateTab]);
  const [syncError, setSyncErrorMsg] = useState<string | null>(null);
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [showUploadCsv, setShowUploadCsv] = useState(false);
  const [showManage, setShowManage] = useState(false);
  const [showAiChat, setShowAiChat] = useState(false);
  const [requestReason, setRequestReason] = useState('');
  const [requestSubmitting, setRequestSubmitting] = useState(false);
  const [requestSubmitted, setRequestSubmitted] = useState(false);
  const [requestDuplicate, setRequestDuplicate] = useState(false);
  const [requestError, setRequestError] = useState<string | null>(null);
  const canSync = accessLevel === 'editor' || accessLevel === 'admin' || accessLevel === 'superadmin';
  const refreshDeals = () => {
    setSyncMeta('loading');

    // Fetch deals (blocks the loader) and touchpoints (background, non-blocking) in parallel.
    // The loader only waits for fetchDeals to complete.
    fetchDeals().then(result => {
      if (result && result.records.length > 0) {
        const transformed = transformRequestResponse(result.records);
        setRequests(transformed);
        setSyncMeta(result.stale ? 'error' : 'success', result.lastSyncedAt ?? undefined);
      } else {
        setSyncMeta('never');
      }
    }).catch(err => {
      console.error('fetchDeals failed:', err);
      setSyncMeta('error');
    });

    // Touchpoints fetch runs in the background — does not block the loader.
    fetchTouchpoints().then(result => {
      console.log('[App] fetchTouchpoints result:', result?.records?.length ?? 0, 'records');
      if (result && result.records.length > 0) {
        const transformed = transformTouchpointResponse(result.records);
        setTouchpoints(transformed);
      }
    }).catch(err => {
      console.error('fetchTouchpoints failed:', err);
    });
  };

  // Fetch live deal data once the user is authenticated and provisioned
  useEffect(() => {
    if (!isSignedIn || accessLevel === 'unregistered' || accessLevel === 'anonymous') return;
    void refreshDeals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn, accessLevel]);
  const handleManualSync = async () => {
    setSyncMeta('loading');
    setSyncErrorMsg(null);
    try {
      // Trigger both reports in parallel
      const results = await Promise.allSettled([triggerSync('all-ondemand-global-requests'), triggerSync('all-ondemand-global-touchpoints')]);

      // Check if at least one succeeded
      const anySucceeded = results.some(r => r.status === 'fulfilled' && r.value);
      if (anySucceeded) {
        // Refresh data from both reports
        void refreshDeals();
      } else {
        // Both failed
        const firstError = results.find(r => r.status === 'rejected');
        const msg = firstError && firstError.status === 'rejected' ? firstError.reason instanceof Error ? firstError.reason.message : 'Sync failed' : 'Sync failed';
        setSyncErrorMsg(msg);
        setSyncMeta('error');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Sync failed';
      setSyncErrorMsg(msg);
      setSyncMeta('error');
    }
  };
  if (!isAuthReady || isAuthLoading) {
    return <div className="flex h-screen items-center justify-center bg-slate-50 text-slate-500 font-sans" data-id="element-3" data-mp="2at1ssxqp9ptl">
        Initializing Adobe sign-in...
      </div>;
  }
  if (!isSignedIn) {
    return <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6 py-10 text-slate-900 font-sans" data-id="element-4" data-mp="2kp54au7fz0t7">
        <div className="w-full max-w-xl rounded-2xl border border-slate-200 bg-white p-8 shadow-sm" data-id="element-5" data-mp="2fr3dfn3rxx76">
          <div className="flex items-center gap-4" data-id="element-6" data-mp="2tkumkudlff2d">
            <img src={ADOBE_LOGO_URL} alt="Adobe OnDemand" className="h-14 w-auto" data-id="element-7" data-mp="2pgwo8lw3e8ad" />
            <div data-id="element-8" data-mp="33gegjb8lk3oh">
              <h1 className="text-2xl font-bold text-slate-900" data-id="element-9" data-mp="33hzbh1ytpomv">OnDemand Team Dashboard</h1>
              <p className="mt-1 text-sm text-slate-500" data-id="element-10" data-mp="2yimdpm6eyx5i">Sign in with Adobe ID</p>
            </div>
          </div>

          <div className="mt-8 space-y-4" data-id="element-11" data-mp="3i4o6aqi4rdmx">
            <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800" data-id="element-12" data-mp="3i1sc9d9el93r">
              Dashboard data is available only after Adobe IMS login.
              <br data-id="element-13" data-mp="38575amdtpnxa" /><br data-id="element-14" data-mp="38571ebapd3s0" />
              <p data-id="element-15" data-mp="3d4jql2p1qmxr">
                1. Click Sign-In<br data-id="element-16" data-mp="3d3qrmnwbx1q6" />
                2. Login using your company account<br data-id="element-17" data-mp="3d6d0qryvbpai" />
                3. Choose profile: Adobe Inc
              </p>
            </div>

            {authError && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-id="element-18" data-mp="344dixxg14h6i">
                {authError}
              </div>}

            <button onClick={() => void signIn()} disabled={isAuthLoading} className="w-full flex items-center justify-center gap-2 rounded-xl bg-red-600 px-6 py-3 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60 transition-colors" data-id="element-19" data-mp="2pd4kqzy29nhr">
              {isAuthLoading ? 'Signing in…' : 'Sign in with Adobe ID'}
            </button>
          </div>
        </div>
      </div>;
  }
  if (accessLevel === 'unregistered') {
    const handleRequestAccess = async () => {
      if (!requestReason.trim()) return;
      setRequestSubmitting(true);
      setRequestError(null);
      setRequestDuplicate(false);
      try {
        await emitEvent('access.requested', {
          email: user?.email ?? '',
          displayName: user?.displayName ?? '',
          reason: requestReason.trim()
        });
        setRequestSubmitted(true);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to submit request';
        if (msg.toLowerCase().includes('already pending')) {
          setRequestDuplicate(true);
        } else {
          setRequestError(msg);
        }
      } finally {
        setRequestSubmitting(false);
      }
    };
    return <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6 py-10 text-slate-900 font-sans" data-id="element-20" data-mp="1u889ibt4up70">
        <div className="w-full max-w-xl rounded-2xl border border-slate-200 bg-white p-8 shadow-sm" data-id="element-21" data-mp="9omnqmpxhxfs">
          <div className="flex items-center gap-4" data-id="element-22" data-mp="doogu7hllijg">
            <img src={ADOBE_LOGO_URL} alt="Adobe OnDemand" className="h-14 w-auto" data-id="element-23" data-mp="ip2m5hrteeds" />
            <div data-id="element-24" data-mp="nof9e3mllngw">
              <h1 className="text-2xl font-bold text-slate-900" data-id="element-25" data-mp="noxhpymjbs56">OnDemand Team Dashboard</h1>
              <p className="mt-1 text-sm text-slate-500" data-id="element-26" data-mp="3m6t81kfg4wqz">Account not provisioned</p>
            </div>
          </div>

          <div className="mt-8 space-y-4" data-id="element-27" data-mp="3r6fl9tv9yczs">
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-id="element-28" data-mp="3r31jonufq7te">
              You are signed in with Adobe ID, but this account is not registered for dashboard
              access. No data is shown until an administrator assigns you a role in the system.
            </div>

            {authError && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-id="element-29" data-mp="38duxurych03d">
                {authError}
              </div>}

            {requestError && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-id="element-30" data-mp="2k56sbk0si2p3">
                {requestError}
              </div>}

            {requestDuplicate && <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-id="element-31" data-mp="2u33145698lk1">
                Your request is already pending review.
              </div>}

            {requestSubmitted ? <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800" data-id="element-32" data-mp="21ddwlhuc63do">
                Your access request is in the queue. You will be notified by email after review.
              </div> : <>
                <textarea value={requestReason} onChange={e => setRequestReason(e.target.value)} placeholder="Describe your role and why you need access" rows={3} className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none" data-id="element-33" data-mp="1dpcqsgojl11y" />
                <button onClick={() => void handleRequestAccess()} disabled={requestSubmitting || !requestReason.trim()} className="w-full flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-6 py-3 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60 transition-colors" data-id="element-34" data-mp="3i0isyqkb5eqn">
                  {requestSubmitting ? 'Submitting…' : 'Request Access'}
                </button>
              </>}

            <button onClick={() => void signOut()} disabled={isAuthLoading} className="w-full flex items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-6 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60 transition-colors" data-id="element-35" data-mp="2uaeqimiuvl18">
              {isAuthLoading ? 'Signing out…' : 'Sign out'}
            </button>
          </div>
        </div>
      </div>;
  }
  return <OrgProvider data-id="element-36" data-mp="2ao3dsb8rzrm1">
    <div className="min-h-screen" data-id="element-37" data-mp="2anlawvrlyf55">
      <Header user={user} accessLevel={accessLevel} isAuthLoading={isAuthLoading} onSignOut={() => void signOut()} onToggleAiChat={() => setShowAiChat(v => !v)} isAiChatOpen={showAiChat} data-id="element-38" data-mp="2aomy1ylwes38" />

      {/* Sync status bar — visible to all; sync button only for editor/admin */}
      <div className="flex items-center gap-3 px-4 py-1.5 bg-slate-50 border-b border-slate-200 text-xs text-slate-500" data-id="element-39" data-mp="2pkkqp6wm5eap">
        <span className="flex items-center gap-1.5" data-id="element-40" data-mp="2tlo5rr6u76av">
          {syncStatus === 'loading' && <><Spinner size={12} data-id="element-41" data-mp="3hc0a6s2vikwp" /><span data-id="element-42" data-mp="3hbifhzvv0eqw">Loading live data…</span></>}
          {syncStatus === 'success' && `✓ Data synced${lastSynced ? ` · ${friendlyTime(lastSynced)}` : ''}`}
          {syncStatus === 'error' && `⚠ Showing deals cached ${lastSynced ? ` · ${friendlyTime(lastSynced)}` : ''}`}
          {syncStatus === 'never' && '— No live data yet; showing sample data'}
          {syncStatus === 'idle' && ''}
        </span>
        {syncError && <span className="text-red-600 ml-2" data-id="element-43" data-mp="1kg72u9haxlo5">{syncError}</span>}
        <div className="ml-auto flex items-center gap-2" data-id="element-44" data-mp="1fgkn9te03tc6">
          {(accessLevel === 'admin' || accessLevel === 'superadmin' || accessLevel === 'viewer') && <button onClick={() => setShowHeatmap(true)} className="px-3 py-1 rounded-md bg-indigo-600 text-white text-xs font-medium hover:bg-indigo-700 transition-colors" data-id="element-45" data-mp="mr3zj91bt0ma">
              Heatmap
            </button>}
          {canOpenManage(accessLevel) && <button onClick={() => setShowManage(true)} className="px-3 py-1 rounded-md bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700 transition-colors" data-id="element-46" data-mp="2d28kggia6for">
              Manage
            </button>}
          {isLiveData && (accessLevel === 'admin' || accessLevel === 'superadmin' || accessLevel === 'editor') && <button onClick={() => setShowUploadCsv(true)} className="px-3 py-1 rounded-md bg-indigo-600 text-white text-xs font-medium hover:bg-indigo-700 transition-colors" data-id="element-47" data-mp="qn01lq7mngnh">
              Upload
            </button>}
          {canSync && <button onClick={() => void handleManualSync()} disabled={syncStatus === 'loading'} className="px-3 py-1 rounded-md bg-slate-700 text-white text-xs font-medium hover:bg-slate-800 disabled:opacity-50 transition-colors flex items-center gap-1.5" data-id="element-48" data-mp="15k8vaqkekwid">
              {syncStatus === 'loading' ? <><Spinner size={12} color="#fff" data-id="element-49" data-mp="1ja2jr7rzxerk" />Syncing…</> : 'Sync'}
            </button>}
        </div>
      </div>

      {showHeatmap && <HeatmapModal requests={requests} onClose={() => setShowHeatmap(false)} data-id="element-50" data-mp="1yg8exszqdvp9" />}

      {showManage && <ManageModal accessLevel={accessLevel} user={user} onClose={() => setShowManage(false)} data-id="element-51" data-mp="1tej0029e0xvw" />}

      {showUploadCsv && <UploadCsvModal onClose={() => setShowUploadCsv(false)} onUploaded={() => void refreshDeals()} data-id="element-52" data-mp="1oiu54c8bc55x" />}

      {showAiChat && <AiChatSidebar onClose={() => setShowAiChat(false)} data-id="element-53" data-mp="2h5y86bnui9f2" />}

      <TabNav currentTab={currentTab} onSwitch={switchTab} data-id="element-54" data-mp="2c6lmvvcmhael" />

      <div className="relative" data-id="element-55" data-mp="2c7dfcxaxas0n">
        {/* Initial page-load overlay — shown while data is being fetched for the first time */}
        {syncStatus === 'loading' && !isLiveData && <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-white/80 min-h-64" data-id="element-56" data-mp="23ds1dg6l1esa">
            <Spinner size={36} color="#4F46E5" data-id="element-57" data-mp="2zyah5h9088y6" />
            <p className="text-sm text-slate-500" data-id="element-58" data-mp="2v57kv1cuelea">Loading dashboard data…</p>
          </div>}

        {currentTab === 'vp' && <>
            <FilterBar tab="vp" state={state.vp} onUpdate={u => updateTab('vp', u as Partial<TabState>)} data-id="element-59" data-mp="ta0cs03nl97l" />
            <VPPanel state={state.vp} onDrillRegion={drillRegion} data-id="element-60" data-mp="y8uvl21mexrp" />
          </>}
        {currentTab === 'sd' && <>
            <FilterBar tab="sd" state={state.sd} onUpdate={u => updateTab('sd', u as Partial<TabState>)} data-id="element-61" data-mp="ogfavwyxvv34" />
            <SDPanel state={state.sd} data-id="element-62" data-mp="ageb6kueutp8" />
          </>}
        {currentTab === 'od' && <>
            <FilterBar tab="od" state={state.od} onUpdate={u => updateTab('od', u as Partial<TabState>)} data-id="element-63" data-mp="fhaditz467sj" />
            <ODPanel state={state.od} data-id="element-64" data-mp="kt3q76kp183" />
          </>}
        {currentTab === 'at' && <AvailabilityTrackerPanel data-id="element-65" data-mp="5je88isuy72c" />}
      </div>
    </div>
    </OrgProvider>;
}
export default function App() {
  return <RequestsProvider data-id="element-66" data-mp="1uy2es3erousg">
      <Dashboard data-id="element-67" data-mp="1zwdqkjomhfdb" />
    </RequestsProvider>;
}